<template>
	<v-card tile elevation="0" style="height: 100%; overflow-y: hidden">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col class="align-center d-flex" cols="10">
					<v-toolbar-title>{{ $t('edit-condition-header') }}</v-toolbar-title>
				</v-col>
				<v-spacer></v-spacer>
				<v-icon @click="closeDrawer" class="white-icon">mdi-close</v-icon>
			</v-row>
		</v-card-title>
		<v-card-text class="pt-4 pb-0 overflow-y-auto" style="height: calc(100vh - 205px)">
			<v-row dense class="ma-0">
				<v-col cols="8">
					<v-select
						:label="$t('severity-of-condition')"
						v-model="selectedSeverity"
						:items="conditionSeverityOptionTemplate"
						item-text="code"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ $t(item.code.toLowerCase()) }}</span>
								</template>
								{{ $t(item.description) }}
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
				<v-col cols="4" class="d-flex justify-center align-center">
					<v-checkbox class="pa-0 ma-0" hide-details color="primary" :label="$t('primary')" v-model="isPrimary" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col>
					<v-textarea
						outlined
						no-resize
						rows="8"
						v-model="managementGoals"
						:label="$t('management-goals')"
						hide-details />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col>
					<v-textarea outlined no-resize rows="9" hide-details v-model="reasonText" :label="$t('reason')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col>
					<v-select
						:label="$t('event-type')"
						v-model="eventType"
						:items="conditionEventTypeOptionTemplate"
						item-text="code"
						outlined
						hide-details
						:menu-props="{ maxHeight: 199, offsetY: true }">
						<template v-slot:item="{ item }">
							<v-tooltip left>
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on" style="width: 100%">{{ $t(item.code) }}</span>
								</template>
								{{ $t(item.description) }}
							</v-tooltip>
						</template>
					</v-select>
				</v-col>
			</v-row>
		</v-card-text>
		<v-container class="px-4">
			<v-row dense class="ma-0">
				<v-col cols="12">
					<InputTimeFormatter
						:inputValue="timeSpent"
						@update:inputValue="(val) => (timeSpent = val)"
						:inputLabel="$t('time-spent')" />
				</v-col>
			</v-row>
			<v-row dense class="ma-0">
				<v-col cols="6" order="1">
					<v-btn @click="closeDrawer()" color="secondary" style="color: black" block>{{ $t('cancel') }}</v-btn>
				</v-col>
				<v-tooltip left v-if="!editButtonEnabled">
					<template v-slot:activator="{ on, attrs }">
						<v-col cols="6" v-bind="attrs" v-on="on" order="2">
							<v-btn disabled block>{{ $t('update') }}</v-btn>
						</v-col>
					</template>
					<span>{{ $t('fill-all-fields-warning') }}</span>
				</v-tooltip>
				<v-col cols="6" v-else order="2">
					<v-btn @click="updateCondition()" color="dark_purple" style="color: white" block>{{ $t('update') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
	</v-card>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import InputTimeFormatter from '../Client/components/InputTimeFormatter.vue';

export default {
	name: 'CCMConditionEdit',
	data () {
		return {
			eventType: '',
			isPrimary: false,
			managementGoals: '',
			reasonText: '',
			selectedSeverity: null,
			timeSpent: 0,
			conditionSeverityOptionTemplate: null,
			conditionEventTypeOptionTemplate: null,
		};
	},
	computed: {
		...mapState({
			ccmConditions: (state) => state.ccmConditions.ccmConditionsByCCMRecordId,
			currentRecord: (state) => state.ccmRecords.ccmRecord,
			currentCondition: (state) => state.ccmConditions.ccmCondition,
			userData: (state) => state.authentication.userData,
		}),
		editButtonEnabled () {
			return (
				this.eventType.trim() &&
				this.reasonText.trim() &&
				this.timeSpent &&
				this.managementGoals.trim() &&
				this.timeSpent <= 7200
			);
		},
	},
	methods: {
		...mapActions({
			updateCCMCondition: 'ccmConditions/updateCCMCondition',
			getConditions: 'ccmConditions/getCCMConditionsByPatientId',
			getConditionSeverityOption: 'ccmRecords/getConditionSeverityOption',
			getConditionEventTypeOption: 'ccmRecords/getConditionEventTypeOption',
		}),
		closeDrawer () {
			this.timeSpent = 0;
			this.reasonText = '';
			this.eventType = '';
			this.$store.commit('SET_UPDATE_DETAILS_DRAWER', false);
			this.$store.commit('SET_EDIT_DETAILS_DRAWER', false);
		},
		async updateCondition () {
			// const additionalDataJSON = {
			// 	managementGoals: this.currentCondition.managementGoals,
			// 	isPrimary: this.currentCondition.isPrimary,
			// 	severity: this.currentCondition.severity,
			// };
			const request = {
				id: this.currentCondition.id,
				body: {
					icD10Code: this.currentCondition.icD10Code,
					name: this.currentCondition.name,
					severity: this.selectedSeverity,
					managementGoals: this.managementGoals,
					isPrimary: this.isPrimary,
					staffId: this.currentCondition.staffId,
					hospitalId: this.currentCondition.hospitalId,
					timeSpentSeconds: parseInt(this.timeSpent),
					condtionTimestampUTC: this.currentCondition.conditionTimestampUTC,
					additionalData: this.currentCondition.additionalData,
					conditionHistory: {
						conditionId: this.currentCondition.id,
						patientId: this.currentCondition.patientId,
						hospitalId: this.currentCondition.hospitalId,
						eventType: this.eventType,
						reason: this.reasonText,
						conditionHistoryTimestampUTC: new Date().toISOString(),
						staffId: this.userData.id,
						performedBy: `${this.userData.firstName} ${this.userData.lastName}`,
						performedByRole: this.userData.roleData.roleName,
						timeSpentSeconds: 0,
						additionalData: '',
					},
					action: this.currentCondition.action,
				},
			};
			await this.updateCCMCondition(request);
			this.getConditions(this.currentRecord.patientId);
			this.closeDrawer();
		},
	},
	async mounted () {
		this.isPrimary = this.currentCondition.isPrimary;
		this.selectedSeverity = this.currentCondition.severity;
		this.managementGoals = this.currentCondition.managementGoals;
		if (!this.conditionSeverityOptionTemplate) {
			const response = await this.getConditionSeverityOption();
			this.conditionSeverityOptionTemplate = response.data;
		}
		if (!this.conditionEventTypeOptionTemplate) {
			const response = await this.getConditionEventTypeOption();
			this.conditionEventTypeOptionTemplate = response.data;
		}
	},
	watch: {
		currentCondition () {
			this.isPrimary = this.currentCondition.isPrimary;
			this.selectedSeverity = this.currentCondition.severity;
			this.managementGoals = this.currentCondition.managementGoals;
		},
	},
	components: { InputTimeFormatter },
};
</script>
